import React, { useEffect, useState } from 'react';
import SecuritySection from './ui/SecuritySection';
import SecurityEmptyState from './ui/SecurityEmptyState';
import { IconLock } from '../../ui/adminIcons';
import { formatTimestamp } from '../securityUtils';

const PAGE_SIZE_OPTIONS = [5, 25, 50, 100];
const DEFAULT_PAGE_SIZE = 5;

function formatReason(reason) {
  if (!reason) return '—';
  return reason.replace(/_/g, ' ');
}

function useDebouncedValue(value, delayMs = 300) {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(timer);
  }, [value, delayMs]);

  return debounced;
}

export default function SecurityBlockedIPsPanel({
  blockedIPs,
  pagination,
  loading,
  unblockBusy,
  onUnblock,
  onFetchBlockedIPs,
  refreshKey = 0,
}) {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const debouncedSearch = useDebouncedValue(search);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, pageSize]);

  useEffect(() => {
    onFetchBlockedIPs({
      page,
      limit: pageSize,
      search: debouncedSearch.trim(),
    });
  }, [page, pageSize, debouncedSearch, refreshKey, onFetchBlockedIPs]);

  const total = pagination?.total ?? 0;
  const totalPages = pagination?.totalPages ?? 1;
  const currentPage = pagination?.page ?? page;
  const rangeStart = total === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const rangeEnd = Math.min(currentPage * pageSize, total);
  const hasSearch = debouncedSearch.trim().length > 0;

  return (
    <SecuritySection title="Blocked IP addresses" icon={<IconLock />}>
      <div className="vx-sec-blocked-panel">
        <div className="vx-sec-blocked-toolbar">
          <input
            type="search"
            className="vx-sec-search-input"
            placeholder="Search by IP address…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Search blocked IP addresses"
          />
          <label className="vx-sec-page-size">
            <span className="vx-sec-page-size-label">Per page</span>
            <select
              className="vx-sec-page-size-select"
              value={pageSize}
              onChange={(e) => setPageSize(Number(e.target.value))}
              aria-label="Blocked IPs per page"
            >
              {PAGE_SIZE_OPTIONS.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </label>
        </div>

        {loading && blockedIPs.length === 0 ? (
          <SecurityEmptyState title="Loading blocked IPs…" />
        ) : blockedIPs.length > 0 ? (
          <>
            <div className="vx-sec-table-wrap admin-table">
              <table className="vx-sec-table">
                <thead>
                  <tr>
                    <th>IP address</th>
                    <th>Reason</th>
                    <th>Blocked</th>
                    <th>Source</th>
                    <th aria-label="Actions" />
                  </tr>
                </thead>
                <tbody>
                  {blockedIPs.map((entry) => (
                    <tr key={entry.ipAddress}>
                      <td>
                        <code className="vx-sec-ip-code">{entry.ipAddress}</code>
                      </td>
                      <td className="vx-sec-muted">{formatReason(entry.reason)}</td>
                      <td className="vx-sec-muted">
                        {entry.blockedAt ? formatTimestamp(entry.blockedAt) : '—'}
                      </td>
                      <td className="vx-sec-muted">{entry.source || '—'}</td>
                      <td>
                        <button
                          type="button"
                          className="btn vx-sec-unblock-btn"
                          onClick={() => onUnblock(entry.ipAddress)}
                          disabled={unblockBusy}
                        >
                          Unblock
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="vx-sec-blocked-pagination">
              <span className="vx-sec-blocked-range">
                {hasSearch
                  ? `Showing ${rangeStart}–${rangeEnd} of ${total.toLocaleString()} matching`
                  : `Showing ${rangeStart}–${rangeEnd} of ${total.toLocaleString()}`}
              </span>
              <div className="vx-sec-blocked-pagination-actions">
                <button
                  type="button"
                  className="btn"
                  disabled={loading || !pagination?.hasPrevPage}
                  onClick={() => setPage((current) => Math.max(1, current - 1))}
                >
                  Previous
                </button>
                <span className="vx-sec-blocked-page-indicator">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  type="button"
                  className="btn"
                  disabled={loading || !pagination?.hasNextPage}
                  onClick={() => setPage((current) => Math.min(totalPages, current + 1))}
                >
                  Next
                </button>
              </div>
            </div>
          </>
        ) : hasSearch ? (
          <SecurityEmptyState title="No blocked IPs match your search" />
        ) : (
          <SecurityEmptyState title="No blocked IPs" />
        )}
      </div>
    </SecuritySection>
  );
}
